import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import originalMoment from 'moment';
import { extendMoment } from 'moment-range';
import queryString from 'query-string';
import includes from 'ramda/src/includes';
import values from 'ramda/src/values';
import DatePicker from '@components/@core/DatePicker';
import ErrorBoundary from '@components/@core/ErrorBoundary';
import EditDropdown from '@components/EditDropdown';
import { exportCsv } from '~/services/util-excel';
import Filter from '@components/@core/Filter';
import ResponsiveTabs from '@components/@core/ResponsiveTabs';
import Tab from '@components/@core/Tab';
import ManageActions from '~/actions/manage';
import { SaveActualsTabs, ReportMenu } from '~/services/config';
import AppActions from '~/actions/app';
import EmployeeList from './OrgEmployeeList';
import OrgChart from './OrgChart';
import { EmployeePdf } from './EmployeesPDF';
import { OrgChartPdf } from './OrgChartsPDF';

const moment = extendMoment(originalMoment);

const SaveActuals = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const defaultCompany = useSelector((state) => state.app.selectedCompany);
    const allCompanies = useSelector((state) => state.app.companies);
    const actuals = useSelector((state) => state.manage.saveactualsReport);
    const toggleModal = (e) => dispatch(AppActions.modalRequest(e));

    const [dateStart, setDateStart] = useState(moment().subtract(1, 'months').startOf('month').format('YYYY-MM-DD'));
    const [dateEnd, setDateEnd] = useState(moment().subtract(1, 'months').endOf('month').format('YYYY-MM-DD'));
    const [active, setActive] = useState(queryString.parse(location.search)?.active || 'employees');
    const [companyId, setCompanyId] = useState(defaultCompany);
    const [employeeId, setEmployeeId] = useState([]);

    const date = useMemo(() => {
        return moment.range(dateStart, dateEnd);
    }, [dateStart, dateEnd]);
    const data = useMemo(() => {
        if (employeeId.length > 0) {
            const users = actuals.users.filter((e) => includes(e.id, employeeId));
            return { ...actuals, users };
        }
        return actuals;
    }, [actuals, employeeId]);

    const handleTab = (e) => {
        navigate({ search: `?active=${e.name}` });
        setActive(e.name);
    };

    const handleDate = (e) => {
        const start = moment(e.start).format('YYYY-MM-DD');
        const end = moment(e.end).format('YYYY-MM-DD');
        dispatch(
            ManageActions.getsaveactualsreportRequest({
                companyId,
                dateStart: start,
                dateEnd: end,
            })
        );
        setDateStart(start);
        setDateEnd(end);
    };

    const handleFilter = (type, e) => {
        if (type === 'company' && e.length === 0 > 0) {
            const ids = (e[0].id < 0 ? allCompanies : e).map((item) => item.id);
            dispatch(
                ManageActions.getsaveactualsreportRequest({
                    companyId: ids,
                    dateStart,
                    dateEnd,
                })
            );
            setCompanyId(ids);
        } else if (type === 'employee') {
            setEmployeeId(e.map((item) => item.id));
        }
    };

    useEffect(() => {
        const ids = companyId[0] < 0 ? allCompanies.map((item) => item.id) : companyId;
        dispatch(
            ManageActions.getsaveactualsreportRequest({
                companyId: ids,
                dateStart,
                dateEnd,
            })
        );
    }, []);

    const handleExcelReport = () => {
        toggleModal({
            type: 'Confirm',
            data: {
                before: {
                    title: 'Confirm',
                    body: 'Would you like to download this reports data to excel?',
                },
            },
            callBack: {
                onYes: () => {
                    handleExcel();
                },
            },
        });
    };

    const handleReportMenu = (e) => {
        switch (e) {
            case 'export':
                handleExcelReport();
                break;
            case 'print':
                if (active === 'chart') {
                    const companies = values(data?.companies);
                    let deps = [];
                    companies.map((company) => {
                        Object.keys(company.departments)?.map((key) => {
                            const department = { ...company?.departments[key], name: key };
                            deps.push(department);
                        });
                    });
                    OrgChartPdf(deps);
                } else {
                    EmployeePdf(data);
                }
                break;
        }
    };
    const handleExcel = () => {
        let dataCsv = [];
        let key = [];
        if (active === 'employees') {
            data?.users?.forEach((item) => {
                const employee = item.name;
                const scorecard = `${item.report?.scorecard_quotas?.saved ?? 0} / ${item.report?.scorecard_quotas?.assigned ?? 0}`;
                const actuals = `${item.report?.scorecard_quotas?.percent ?? 0} %`;
                const career = `${item.report?.career_quotas?.saved ?? 0} / ${item.report?.career_quotas?.assigned ?? 0}`;
                const cactuals = `${item.report?.career_quotas?.percent ?? 0} %`;
                const certification = `${item.report?.certification_quotas?.saved ?? 0} / ${item.report?.certification_quotas?.assigned ?? 0}`;
                const ceractuals = `${item.report?.certification_quotas?.percent ?? 0} %`;
                const total = `${item.report?.all_quotas?.percent ?? 0} %`;
                const AcutalsData = {
                    Employee: employee,
                    Scorecard: scorecard,
                    'Scorecard Actuals': actuals,
                    Career: career,
                    'Career Actuals': cactuals,
                    Certification: certification,
                    'Certification Actuals': ceractuals,
                    Total: total,
                };
                dataCsv.push(AcutalsData);
            });
            key = ['Employee', 'Scorecard', 'Quptas Actual', 'Career', 'Quotas Actuals', 'Certification', 'Quotas Actuals', 'Total'];
        } else {
            const companies = values(data?.companies);
            let deps = [];
            companies.map((company) => {
                Object.keys(company.departments)?.map((key) => {
                    const department = { ...company?.departments[key], name: key };
                    deps.push(department);
                });
            });
            deps.forEach((item) => {
                const departments = item.name;
                const teams = item?.totals?.teams ?? 0;
                const employees = item?.totals?.total_users ?? 0;
                const AcutalsData = {
                    Departments: departments,
                    Teams: teams,
                    Employees: employees,
                };
                dataCsv.push(AcutalsData);
            });
            key = ['Departments', 'Teams', 'Employees'];
        }
        exportCsv(dataCsv, key, `Save Actuals Report`, true);
    };

    return (
        <ErrorBoundary className="mng-save-actual-report">
            <Filter onChange={handleFilter} />
            <div className="card">
                <div className="d-h-between mb-3">
                    <span className="dsl-b22 bold">Save Actuals Report</span>
                    <div className="d-flex">
                        <DatePicker
                            calendar="range"
                            append="caret"
                            format="MMM YY"
                            align="right"
                            as="span"
                            value={date}
                            disabledDate={(e) => moment(e).isAfter(moment().startOf('month'))}
                            onSelect={handleDate}
                        />
                        <EditDropdown className="ml-3" dataCy="editDropdown" options={ReportMenu} onChange={handleReportMenu} />
                    </div>
                </div>
                <ResponsiveTabs active={active} data={SaveActualsTabs} getId={(e) => e.name} getValue={(e) => e.label} onTab={handleTab} />
                <Tab eventKey="employees" active={active}>
                    <EmployeeList data={data} />
                </Tab>
                <Tab eventKey="chart" active={active}>
                    <OrgChart data={data} />
                </Tab>
            </div>
        </ErrorBoundary>
    );
};

export default SaveActuals;
